import {Close, Logout} from '@mui/icons-material';
import {usePersona} from 'src/permissions/hooks';
import {AppBar, Box, Divider, Drawer, IconButton, Typography} from '@mui/material';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Link, NavLink, useLocation} from 'react-router-dom';
import {store} from 'src/app/store';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {LogoBlack} from 'src/components/icons/LogoBlack';
import {LogoWhite} from 'src/components/icons/LogoWhite';
import {useLogout} from 'src/hooks/useLogout';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {getEvDriverNameFromAuth} from 'src/utils/userNameFormatter';
import {AuthUserDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {MainNavigation} from '../MainNavigation';
import {LangSelect} from '../LangSelect';
import {
    appBarSx,
    drawerSx,
    hideDrawerButtonSx,
    imageWrapperSx,
    logoWrapperMobileSx,
    logoWrapperSx,
    profileLogoSx,
    profileNameSx,
    profileWrapperSx,
    settingsButtonSx,
} from './AMPDrawer.sx';
import {AMPDrawerType} from './AMPDrawer.types';

export const AMPDrawer: FC<AMPDrawerType> = ({handleCloseDrawer, open, drawerVariant}) => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const {authInfo} = useAppSelector((state) => state.common);
    // The drawer and the phone's More screen end a session the same way, so the handler lives in one
    // hook rather than in both.
    const handleLogOut = useLogout();

    const {pathname} = useLocation();
    // Was three separate reads of `window.innerWidth`, each taken once and never re-evaluated: after
    // a resize or a rotation the menu was still deciding by the width the page happened to load at.
    const {isCompact} = useScreenTier();

    const [currentPath, setCurrentPath] = useState<string>('');
    const [initials, setInitials] = useState<string>('');

    const persona = usePersona();
    const isSuperAdmin = persona.isPlatformAdmin;
    const isBusiness = persona.isBusinessOwner;
    const isCompanyUser = persona.isCompany;
    // Back-office staff who are not the owner. The manager kinds are separate values now, but this
    // menu wants them together, so it asks the persona rather than naming them.
    const isManager = persona.isBackOffice && !persona.isBusinessOwner && !persona.isPlatformAdmin;

    const link = useMemo(() => {
        if (authInfo?.roles?.length && isBusiness) {
            return AppRouteSpace([
                AppRouteEnum.app,
                AppRouteEnum.users,
                AppRouteEnum.business,
                String(businessId),
            ]);
        }
        if (authInfo?.roles?.length && isManager) {
            return AppRouteSpace([AppRouteEnum.app, AppRouteEnum.technicalTeam, authInfo.id]);
        }
        return AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, authInfo.id]);
        // On the account, not on mount: computed once, this pointed at /app/users/undefined whenever
        // the drawer rendered before the profile arrived.
    }, [authInfo, businessId, isBusiness, isManager]);

    const userName = useCallback(() => {
        if (!Object.keys(authInfo).length) {
            return '';
        }

        if (isCompanyUser) {
            return authInfo.company?.name ?? getEvDriverNameFromAuth(authInfo as AuthUserDTO);
        }

        if (isBusiness) {
            return authInfo.business?.name ?? '';
        }

        if (!isBusiness && !isSuperAdmin) {
            // Not everyone has a first and last name: a company's account has the company's,
            // an employee may carry only their company's. Concatenating the two fields directly
            // produced "null null" for exactly those accounts. This is the same formatter the
            // sessions and transactions tables already use, so one account reads the same everywhere.
            return getEvDriverNameFromAuth(authInfo as AuthUserDTO);
        }

        // The role code used to be used as a translation key, with nine matching entries in each
        // lang file. A business-defined name has no key, and react-intl would render the raw code.
        // The server sends display names for exactly this, and several roles are ordinary now - joining
        // them beats picking one, which would present a person as half of what they are.
        const roleNames = (store.getState().common.access?.roles ?? []).map((role: {name: string}) => role.name);
        return roleNames.length ? roleNames.join(', ') : '';
    }, [authInfo]);

    useEffect(() => {
        setCurrentPath(pathname);
        if (isCompact && currentPath !== pathname) {
            void handleCloseDrawer();
        }
    }, [pathname, isCompact]);
    // Initials come from the name actually on screen. Taken from firstName/lastName they were blank
    // for any account without both, which is every company and some employees.
    useEffect(() => {
        const words = String(userName() ?? '').trim().split(/\s+/).filter(Boolean);
        setInitials(words.slice(0, 2).map((word) => word[0]?.toUpperCase() ?? '').join(''));
    }, [userName]);

    return (
        <AppBar
            position="static"
            sx={appBarSx}
        >
            <Drawer
                variant={drawerVariant}
                open={open}
                anchor="left"
                sx={drawerSx}
                onClose={handleCloseDrawer}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                <Box>
                    <Box sx={logoWrapperSx}>
                        {isCompact && (
                            <Box>
                                <IconButton
                                    onClick={handleCloseDrawer}
                                    sx={hideDrawerButtonSx}
                                >
                                    <Close/>
                                </IconButton>
                            </Box>
                        )}
                        <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations])}>
                            <LogoWhite height={'32'}/>
                        </NavLink>
                        <LangSelect
                            white
                            short
                        />
                    </Box>
                    <Box sx={logoWrapperMobileSx}>
                        {isCompact && (
                            <Box>
                                <IconButton
                                    onClick={handleCloseDrawer}
                                    sx={hideDrawerButtonSx}
                                >
                                    <Close/>
                                </IconButton>
                            </Box>
                        )}
                        <NavLink to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations])}>
                            <LogoBlack/>
                        </NavLink>
                        <LangSelect short/>
                    </Box>
                    <MainNavigation/>
                </Box>
                <Box>
                    <Divider light/>
                    <Box sx={profileWrapperSx}>
                        {!isSuperAdmin ? (
                            <Link to={link}>
                                <Box sx={profileNameSx}>
                                    {authInfo.avatar ? (
                                        <Box sx={imageWrapperSx}>
                                            <img
                                                src={authInfo.avatar.url}
                                                alt={`${authInfo.firstName} ${authInfo.lastName}`}
                                            />
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="caption"
                                            sx={profileLogoSx}
                                        >
                                            {initials}
                                        </Typography>
                                    )}
                                    <Typography variant="subtitle1">{userName()}</Typography>
                                </Box>
                            </Link>
                        ) : (
                            <Box sx={profileNameSx}>
                                {authInfo.avatar ? (
                                    <Box sx={imageWrapperSx}>
                                        <img
                                            src={authInfo.avatar.url}
                                            alt={`${authInfo.firstName} ${authInfo.lastName}`}
                                        />
                                    </Box>
                                ) : (
                                    <Typography
                                        variant="caption"
                                        sx={profileLogoSx}
                                    >
                                        {initials}
                                    </Typography>
                                )}
                                <Typography variant="subtitle1">{userName()}</Typography>
                            </Box>
                        )}
                        <IconButton
                            sx={settingsButtonSx}
                            onClick={handleLogOut}
                        >
                            <Logout/>
                        </IconButton>
                    </Box>
                </Box>
            </Drawer>
        </AppBar>
    );
};
